import { Database, Eye, EyeOff, Lock, SlidersHorizontal, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import { RequireCapability } from "@/components/RequireCapability";
import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { changePassword, patchMe } from "@/lib/auth/auth-api";
import { useAuth } from "@/lib/auth/auth-context";
import { useAutosave } from "@/lib/hooks/useAutosave";
import { SUPPORTED_LANGUAGES, type Language } from "@/lib/i18n/i18n";
import { queryKeys } from "@/lib/query/keys";
import { cn } from "@/lib/utils/cn";
import type { ApiError } from "@/lib/api/unwrap";

/** Account settings: identity, password, and preferences. */
export function SettingsPage() {
  return (
    <RequireCapability required={["authenticated"]}>
      <SettingsContent />
    </RequireCapability>
  );
}

const SECTIONS = [
  { id: "account", icon: User, labelKey: "settings.navAccount" },
  { id: "security", icon: Lock, labelKey: "settings.navSecurity" },
  { id: "preferences", icon: SlidersHorizontal, labelKey: "settings.navPreferences" },
] as const;

function SettingsContent() {
  const { t } = useTranslation();
  const active = useScrollSpy(SECTIONS.map((s) => s.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
        <p className="mt-1 text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      {/* Mobile section jump nav: the desktop rail is hidden below md. */}
      <nav
        aria-label={t("settings.title")}
        className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 md:hidden"
      >
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className={cn(
              "inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-sm font-semibold transition-colors",
              active === s.id
                ? "border-transparent bg-secondary text-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            <s.icon className="size-4" aria-hidden />
            {t(s.labelKey)}
          </a>
        ))}
      </nav>

      <div className="grid gap-8 md:grid-cols-[210px_1fr]">
        <nav className="sticky top-4 hidden h-fit flex-col gap-0.5 md:flex">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold transition-colors",
                active === s.id
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              <s.icon className="size-4" aria-hidden />
              {t(s.labelKey)}
            </a>
          ))}
        </nav>

        <div className="flex flex-col gap-5">
          <AccountSection />
          <PasswordSection />
          <PreferencesSection />
        </div>
      </div>
    </div>
  );
}

// --- Account (identity, autosaved) -------------------------------------------

interface Identity {
  first_name: string;
  last_name: string;
  position: string;
}

function AccountSection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();

  const autosave = useAutosave<Identity>({
    save: async (data) => {
      await patchMe(data);
      await qc.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
    onError: () => toast.error(t("settings.saveError")),
  });

  const [values, setValues] = useState<Identity>({
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    position: user?.position ?? "",
  });

  const patch = (p: Partial<Identity>) => {
    const next = { ...values, ...p };
    setValues(next);
    autosave.save(next);
  };

  const field = (key: keyof Identity, labelKey: string) => {
    const id = `settings-${key}`;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>{t(labelKey)}</Label>
        <Input id={id} value={values[key]} onChange={(e) => patch({ [key]: e.target.value })} />
      </div>
    );
  };

  return (
    <Card id="account" className="scroll-mt-6">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{t("settings.account.title")}</CardTitle>
            <CardDescription>{t("settings.account.desc")}</CardDescription>
          </div>
          <SaveStatus status={autosave.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {field("first_name", "settings.account.firstName")}
          {field("last_name", "settings.account.lastName")}
          {field("position", "settings.account.position")}
          <div className="space-y-1.5">
            <Label htmlFor="settings-email">{t("settings.account.email")}</Label>
            <Input
              id="settings-email"
              // `contact_email`: a training participant's `email` is a
              // fabricated identifier on a non-routable domain.
              value={user?.contact_email ?? user?.email ?? ""}
              disabled
              className="bg-muted text-muted-foreground"
            />
            <p className="text-xs text-muted-foreground">{t("settings.account.emailHint")}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Password (explicit submit) ----------------------------------------------

function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next1 !== next2) {
      setErrors({ new_password2: t("settings.password.mismatch") });
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await changePassword({
        old_password: current,
        new_password1: next1,
        new_password2: next2,
      });
      toast.success(t("settings.password.success"));
      setCurrent("");
      setNext1("");
      setNext2("");
    } catch (err) {
      const apiError = err as ApiError;
      if (apiError.fieldErrors) {
        const fieldErrors: Record<string, string> = {};
        for (const [key, msgs] of Object.entries(apiError.fieldErrors)) {
          fieldErrors[key] = msgs.join(" ");
        }
        setErrors(fieldErrors);
      }
      toast.error(apiError.detail ?? t("settings.password.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card id="security" className="scroll-mt-6">
      <CardHeader>
        <CardTitle className="text-lg">{t("settings.password.title")}</CardTitle>
        <CardDescription>{t("settings.password.desc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="max-w-md space-y-4" noValidate>
          <PasswordField
            id="pw-current"
            label={t("settings.password.current")}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            error={errors.old_password}
          />
          <PasswordField
            id="pw-new1"
            label={t("settings.password.new")}
            value={next1}
            onChange={setNext1}
            autoComplete="new-password"
            error={errors.new_password1}
          />
          <PasswordField
            id="pw-new2"
            label={t("settings.password.confirm")}
            value={next2}
            onChange={setNext2}
            autoComplete="new-password"
            error={errors.new_password2}
          />
          <Button type="submit" disabled={saving || !current || !next1 || !next2}>
            {saving ? t("settings.password.submitting") : t("settings.password.submit")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  autoComplete,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  error?: string;
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          autoComplete={autoComplete}
          aria-invalid={error !== undefined}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? t("auth.hidePassword") : t("auth.showPassword")}
          className="absolute right-2 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground"
        >
          {show ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

// --- Preferences (language + consents link) ----------------------------------

function PreferencesSection() {
  const { t, i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? i18n.language) as string;

  return (
    <Card id="preferences" className="scroll-mt-6">
      <CardHeader>
        <CardTitle className="text-lg">{t("settings.preferences.title")}</CardTitle>
        <CardDescription>{t("settings.preferences.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>{t("settings.preferences.language")}</Label>
          <div className="inline-flex gap-1 rounded-lg border bg-secondary p-1">
            {SUPPORTED_LANGUAGES.map((lng: Language) => (
              <button
                key={lng}
                type="button"
                aria-pressed={current.startsWith(lng)}
                onClick={() => void i18n.changeLanguage(lng)}
                className={cn(
                  "rounded-md px-4 py-1.5 text-sm font-semibold transition-colors",
                  current.startsWith(lng)
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(`language.${lng}`)}
              </button>
            ))}
          </div>
        </div>

        <Link
          to="/consents"
          className="flex items-center gap-3.5 rounded-xl border bg-secondary/50 p-4 transition-colors hover:bg-secondary"
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-lg border bg-background text-muted-foreground">
            <Database className="size-5" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-foreground">
              {t("settings.preferences.consentsTitle")}
            </span>
            <span className="block text-xs text-muted-foreground">
              {t("settings.preferences.consentsDesc")}
            </span>
          </span>
          <span className="shrink-0 text-sm font-bold text-primary">
            {t("settings.preferences.consentsCta")} →
          </span>
        </Link>
      </CardContent>
    </Card>
  );
}

// --- Scrollspy for the section nav -------------------------------------------

function useScrollSpy(ids: readonly string[]): string {
  const [active, setActive] = useState(ids[0] ?? "");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px" },
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids]);
  return active;
}
